import api from '@/api/api';
import {capitalizeFilter, isNull} from "@/utils/formatting";
import {defaultList} from "@/utils/utils";
import {cloneDeep, isNumber, identity} from "lodash";


export class ActionGenerator {
    constructor(api, mutation, name, {plural, filter, afterUpdateFunction, afterCreateFunction} = {}) {
        this.api = api;
        this.mutation = mutation;
        this.mutationList = mutation + '_LIST';
        this.mutationFields = mutation + '_FIELDS';
        this.mutationBackup = mutation + '_BACKUP';
        this.mutationProperty = mutation + '_PROPERTY';
        this.name = name;
        this.nameBackup = name + 'Backup';
        this.plural = plural;
        this.stateFields = this.name + 'Fields';
        this.ongoingOptions = false;
        this.filter = filter || identity;
        this.afterUpdateFunction = afterUpdateFunction || identity;
        this.afterCreateFunction = afterCreateFunction || identity;
    }

    generateActions() {
        const capitalizedMethod = capitalizeFilter(this.name);
        const setElement = 'set' + capitalizedMethod;
        const setElementProperty = 'set' + capitalizedMethod + 'Property';
        const getElement = 'get' + capitalizedMethod;
        const createElement = 'create' + capitalizedMethod;
        const removeElement = 'remove' + capitalizedMethod;
        const updateElement = 'update' + capitalizedMethod;
        const updateOrCreateElement = 'updateOrCreate' + capitalizedMethod;
        const editElement = 'edit' + capitalizedMethod;
        const restoreEleemnt = 'restore' + capitalizedMethod;
        const getList = this.plural ? 'get' + capitalizeFilter(this.plural) : getElement + 's';
        const getOptions = getElement + 'Options';
        return {
            [getElement]: async (context, pk) => {
                await this.getOptions(context, getOptions);
                return this.getElement(context, getElement, pk);
            },
            [setElement]: (context, data) => context.commit(this.mutation, data),
            [setElementProperty]: (context, data) => context.commit(this.mutationProperty, data),
            [createElement]: ({dispatch}, data) => {
                const correctData = this.filter(data);
                return this.api[createElement](correctData)
                    .then((response) => {
                        this.afterCreateFunction(response, dispatch);
                        return response.data;
                    });
            },
            [updateElement]: ({dispatch}, {pk, data}) => {
                this.raiseIfNotDefined(updateElement);
                if (isNull(pk) || isNull(data)) {
                    throw 'Method ' + updateElement + ' needs at least "pk" and "data". Received pk:' +
                    pk + ' and data: '+ data ;
                }
                const correctData = this.filter(data);
                return this.api[updateElement](pk, correctData)
                    .then((response) => {
                        this.afterUpdateFunction(response, dispatch, pk);
                        return response.data;
                    });
            },
            [updateOrCreateElement]: ({dispatch}, data) => {
                if (isNull(data.id)) {
                    return dispatch(createElement, data);
                } else {
                    return dispatch(updateElement, {pk: data.id, data});
                }
            },
            [removeElement]: (context) => this.removeElement(context),
            [editElement]: ({commit, state}) => commit(this.mutationBackup, cloneDeep(state[this.name])),
            [restoreEleemnt]: ({commit, state}) => commit(this.mutation, state[this.nameBackup]),
            [getList]: async (context, params) => {
                await this.getOptions(context, getOptions);
                return this.getElementList(context, getList, params);
            },
            [getOptions]: (context) => this.getOptions(context, getOptions),
        };
    }

    getElement({ commit }, apiCall, pk) {
        this.raiseIfNotDefined(apiCall);
        return this.api[apiCall](pk)
            .then(response => commit(this.mutation, response.data))
            .catch(error => {
                commit(this.mutation, null);
                throw error;
            } );
    }

    getElementList({ commit }, apiCall, params)  {
        this.raiseIfNotDefined(apiCall);
        return this.api[apiCall](params)
            .then(response => this.storeList(response, commit))
            .catch(() => this.storeEmptyList(commit));
    }

    async getOptions({ commit, state }, apiCall) {
        this.raiseIfNotDefined(apiCall);
        if (!isNull(state[this.stateFields]) || this.ongoingOptions) {
            return Promise.resolve(null);
        }
        // only make options request first time and wait for completion
        this.ongoingOptions = true;
        return this.api[apiCall]()
            .then(response => commit(this.mutationFields, response.data.actions.POST))
            .catch(() => commit(this.mutationFields, null))
            .finally(() => this.ongoingOptions = false);
    }

    removeElement({ commit }) {
        commit(this.mutation, null);
    }

    storeList(response, commit) {
        commit(this.mutationList, response.data);
    }

    storeEmptyList(commit) {
        commit(this.mutationList, defaultList());
    }

    raiseIfNotDefined(apiCall) {
        if(!this.api.hasOwnProperty(apiCall)) {
            throw 'Api has not method ' + apiCall;
        }
    }
}

const deleteEmptyValue = (object, field, empty_value = '') => {
    if (object[field] === empty_value) {
        delete object[field];
    }
};

const patientOptions = {
    filter: (data) => {
        let dataToSend = cloneDeep(data);
        if (data.hasOwnProperty('center') && !isNull(dataToSend.center) && !isNumber(dataToSend.center) ) {
            dataToSend.center = dataToSend.center.id;
        }
        deleteEmptyValue(dataToSend, 'birthday');
        deleteEmptyValue(dataToSend, 'infusion_number');
        return dataToSend;
    },
    afterUpdateFunction: (response, dispatch, pk) => {
        dispatch('getPatient', pk);
    }
};

const surgeryOptions = {
    plural: 'surgeries',
    filter: (data) => {
        let dataToSend = cloneDeep(data);
        if (dataToSend.description.hasOwnProperty('id')) {
            dataToSend.description = dataToSend.description.id;
        } else {
            dataToSend.description = dataToSend.description.surgery_type + '|' +  dataToSend.description.description;
        }
        deleteEmptyValue(dataToSend, 'date');
        deleteEmptyValue(dataToSend, 'type');
        deleteEmptyValue(dataToSend, 'anesthesia_type');
        deleteEmptyValue(dataToSend, 'hospitalization_date');
        deleteEmptyValue(dataToSend, 'basal_date');
        deleteEmptyValue(dataToSend, 'previous_treatment_date');
        return dataToSend;
    },
    afterUpdateFunction: (response, dispatch) => {
        dispatch('getPatient', response.data.patient);
    },
    afterCreateFunction: (response, dispatch) => {
        dispatch('getPatient', response.data.patient);
    },
};

const previousSurgeryOptions = {
    plural: 'previousSurgeries',
    filter: (data) => {
        const dataToSend = cloneDeep(data);
        deleteEmptyValue(dataToSend, 'type');
        return dataToSend;
    },
    afterUpdateFunction: (response, dispatch) => {
        dispatch('getPatient', response.data.patient);
    },
    afterCreateFunction: (response, dispatch) => {
        dispatch('getPatient', response.data.patient);
    },
};

const postSurgeryOptions = {
    plural: 'postSurgeries',
    filter: (data) => {
        const dataToSend = cloneDeep(data);
        deleteEmptyValue(dataToSend, 'date');
        return dataToSend;
    },
    afterUpdateFunction: (response, dispatch) => {
        dispatch('getSurgery', response.data.surgery);
    },
    afterCreateFunction: (response, dispatch) => {
        dispatch('getSurgery', response.data.surgery);
    },
};

const finalEvaluationOptions = {
    filter: (data) => {
        const dataToSend = cloneDeep(data);
        deleteEmptyValue(dataToSend, 'discharge_patient_date');
        deleteEmptyValue(dataToSend, 'hospital_readmission_date');
        return dataToSend;
    },

};

const actions = {
    getCurrentUser({ commit }) {
        return api.getCurrentUser()
            .then((response) => {
                commit('STORE_CURRENT_USER', response.data);
                return response.data;
            })
            .catch(error => {
                commit('STORE_CURRENT_USER', null);
                return Promise.reject(error);
            });
    },
    ... new ActionGenerator(api, 'STORE_USER', 'user').generateActions(),
    ... new ActionGenerator(api, 'STORE_PATIENT', 'patient', patientOptions).generateActions(),
    ... new ActionGenerator(api, 'STORE_SURGERY', 'surgery', surgeryOptions).generateActions(),
    ... new ActionGenerator(api, 'STORE_CENTER', 'center').generateActions(),
    ... new ActionGenerator(api, 'STORE_SURGERY_DESCRIPTION', 'surgeryDescription').generateActions(),
    ... new ActionGenerator(api, 'STORE_BLOOD_TEST', 'bloodTest').generateActions(),
    ... new ActionGenerator(api, 'STORE_NOVOEIGHT_TREATMENT', 'novoeightTreatment').generateActions(),
    ... new ActionGenerator(api, 'STORE_TRANSFUSION', 'transfusion').generateActions(),
    ... new ActionGenerator(api, 'STORE_ADJUVANT_TREATMENT', 'adjuvantTreatment').generateActions(),
    ... new ActionGenerator(api, 'STORE_PREVIOUS_SURGERY', 'previousSurgery',
        previousSurgeryOptions).generateActions(),
    ... new ActionGenerator(api, 'STORE_POST_SURGERY', 'postSurgery',
        postSurgeryOptions).generateActions(),
    ... new ActionGenerator(api, 'STORE_FINAL_EVALUATION', 'finalEvaluation', finalEvaluationOptions).generateActions(),

    async deleteNovoeightTreatment({dispatch}, {id, surgery}) {
        return dispatch('deleteElementAndRefreshSurgery', {apiFunc: api.deleteNovoeightTreatment,id, surgery});
    },
    async deleteBloodTest({dispatch}, {id, surgery}) {
        return dispatch('deleteElementAndRefreshSurgery', {apiFunc: api.deleteBloodTest,id, surgery});
    },
    async deleteTransfusion({dispatch}, {id, surgery}) {
        return dispatch('deleteElementAndRefreshSurgery', {apiFunc: api.deleteTransfusion,id, surgery});
    },
    async deleteElementAndRefreshSurgery({dispatch}, {apiFunc, id, surgery}) {
        return apiFunc(id)
            .then(() => {
                return dispatch('getSurgery', surgery);
            })
            .catch(error => {
                return Promise.reject(error);
            });
    },

    async updateAdjuvantTreatment(context, {surgery, data}) {
        const dataWithSurgery = data.map(treatment => {
            return {
                surgery,
                ...treatment,
            };
        });
        return api.updateAdjuvantTreatmentInSurgery(surgery, dataWithSurgery);
    },
};


export default actions;